import { useState, useEffect, useCallback } from 'react';
import { useFavorites } from '../contexts/FavoritesContext';
import { api } from '../services/api';
import type { Product } from '../types';
import { devError } from '@/utils/devLog';

export interface UseFavoriteProductsOptions {
  /** Si false, pas d’appel réseau (ex. menu fermé, utilisateur déconnecté). */
  enabled: boolean;
  /**
   * Attendre que le contexte favoris ait fini de charger les ids
   * (évite un premier fetch vide puis un second).
   */
  waitForFavoritesContext?: boolean;
  /** Ne garder que les produits avec `est_validé` (ex. liste mobile). */
  onlyValidated?: boolean;
  /**
   * Quand `enabled` passe à false, vider la liste.
   * Mettre false pour le menu header (garder le dernier chargement à la fermeture).
   */
  clearWhenDisabled?: boolean;
}

/**
 * Charge les détails produits pour les ids favoris via `getProductsByIds` (cache API partagé).
 */
export function useFavoriteProducts({
  enabled,
  waitForFavoritesContext = false,
  onlyValidated = false,
  clearWhenDisabled = true,
}: UseFavoriteProductsOptions): {
  products: Product[];
  loading: boolean;
  error: boolean;
  refetch: () => Promise<void>;
} {
  const { favorites, loading: favoritesContextLoading } = useFavorites();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchProducts = useCallback(async () => {
    const contextReady = !waitForFavoritesContext || !favoritesContextLoading;
    const shouldRun = enabled && contextReady;

    if (!shouldRun) {
      if (!enabled && clearWhenDisabled) {
        setProducts([]);
        setLoading(false);
        setError(false);
      } else if (enabled && waitForFavoritesContext && favoritesContextLoading) {
        setLoading(true);
      }
      return;
    }

    if (favorites.length === 0) {
      setProducts([]);
      setError(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    try {
      let list = await api.getProductsByIds(favorites);
      if (onlyValidated) {
        list = list.filter((p) => p.est_validé);
      }
      setProducts(list);
    } catch (e) {
      devError('Failed to load favorite products', e);
      setError(true);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    waitForFavoritesContext,
    favoritesContextLoading,
    favorites,
    onlyValidated,
    clearWhenDisabled,
  ]);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, refetch: fetchProducts };
}
